import {
    SendingDomainMethod,
    SendingDomainStatus,
} from "@common/types/domains/emailSending";
import { z } from "zod";

/**
 * Lowercase ASCII hostname: at least two labels, each 1–63 chars, no scheme,
 * port, path, or trailing dot. IDNs must be punycode-encoded by the client.
 */
const DOMAIN_REGEX =
    /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)+$/;

export const sendingDomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .regex(DOMAIN_REGEX, "Must be a valid lowercase ASCII (punycode) domain");

export const sendingDomainDnsRecordSchema = z.object({
    host: z.string(),
    type: z.string(),
    data: z.string(),
    valid: z.boolean(),
});

export const sendingDomainResponseSchema = z.object({
    id: z.string().uuid(),
    workspaceId: z.string().uuid().nullable(),
    organizationId: z.string().uuid().nullable(),
    domain: z.string(),
    method: z.nativeEnum(SendingDomainMethod),
    status: z.nativeEnum(SendingDomainStatus),
    verifiedAt: z.coerce.date().nullable(),
    lastVerificationError: z.string().nullable(),
    sendgridDomainId: z.string().nullable(),
    dnsRecords: z.array(sendingDomainDnsRecordSchema).nullable(),
    smtpHost: z.string().nullable(),
    smtpPort: z.number().int().nullable(),
    smtpSecure: z.boolean().nullable(),
    smtpUsername: z.string().nullable(),
    hasSmtpPassword: z.boolean(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});

export type SendingDomainResponse = z.infer<typeof sendingDomainResponseSchema>;

export const listSendingDomainsResponseSchema = z.object({
    domains: z.array(sendingDomainResponseSchema),
});

export type ListSendingDomainsResponse = z.infer<
    typeof listSendingDomainsResponseSchema
>;

const createDnsSendingDomainSchema = z.object({
    method: z.literal(SendingDomainMethod.dns),
    domain: sendingDomainSchema,
});

const createSmtpSendingDomainSchema = z.object({
    method: z.literal(SendingDomainMethod.smtp),
    domain: sendingDomainSchema,
    smtpHost: z.string().trim().min(1).max(253),
    smtpPort: z.number().int().min(1).max(65535).default(587),
    smtpSecure: z.boolean(),
    smtpUsername: z.string().trim().min(1).max(320),
    smtpPassword: z.string().min(1).max(1024),
});

const createGmailSendingDomainSchema = z.object({
    method: z.literal(SendingDomainMethod.gmail),
    code: z.string().trim().min(1).max(2048),
});

export const createSendingDomainSchema = z.discriminatedUnion("method", [
    createDnsSendingDomainSchema,
    createSmtpSendingDomainSchema,
    createGmailSendingDomainSchema,
]);

export type CreateSendingDomainRequest = z.infer<
    typeof createSendingDomainSchema
>;

export const updateSendingDomainSchema = z.object({
    smtpHost: z.string().trim().min(1).max(253).optional(),
    smtpPort: z.number().int().min(1).max(65535).optional(),
    smtpSecure: z.boolean().optional(),
    smtpUsername: z.string().trim().min(1).max(320).optional(),
    smtpPassword: z.string().min(1).max(1024).optional(),
});

export type UpdateSendingDomainRequest = z.infer<
    typeof updateSendingDomainSchema
>;
